import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Modal, Spinner, Alert, Badge, ButtonGroup } from 'react-bootstrap';
import authConfig from '../../authConfig';
import VideoPlayer from './VideoPlayer';
import SecureFileLink from '../meeting/SecureFileLink';

const MemberTrainingList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [completionFilter, setCompletionFilter] = useState('All');

    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoTrainingId, setVideoTrainingId] = useState(null);
    const [videoFileId, setVideoFileId] = useState(null);
    const [videoTitle, setVideoTitle] = useState('');

    const [showHtmlModal, setShowHtmlModal] = useState(false);
    const [htmlContent, setHtmlContent] = useState('');
    const [htmlTitle, setHtmlTitle] = useState('');

    const [completions, setCompletions] = useState([]);
    const [loadingCompletions, setLoadingCompletions] = useState(false);

    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [completeTarget, setCompleteTarget] = useState(null);
    const [submittingCompletion, setSubmittingCompletion] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(authConfig.api.urls.getMemberTrainingForMember, {
                    credentials: 'include'
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setItems(Array.isArray(data) ? data : []);
            } catch (err) {
                if (!cancelled) setError('Failed to load training items');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadCompletions = async () => {
            setLoadingCompletions(true);
            try {
                const res = await fetch(authConfig.api.urls.getMemberTrainingCompletionsForMember, { credentials: 'include' });
                if (!res.ok) {
                    // treat as no completions (not authenticated as member)
                    setCompletions([]);
                } else {
                    const data = await res.json();
                    if (!cancelled) setCompletions(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                if (!cancelled) setCompletions([]);
            } finally {
                if (!cancelled) setLoadingCompletions(false);
            }
        };
        loadCompletions();
        return () => { cancelled = true; };
    }, []);

    const categories = ['All', ...Array.from(new Set(items.map(i => i.category || 'Unspecified'))).sort()];

    // Sort items by category then title for consistent display
    const sortedItems = items.slice().sort((a, b) => {
        const ca = (a.category || 'Unspecified').toString().toLowerCase();
        const cb = (b.category || 'Unspecified').toString().toLowerCase();
        if (ca < cb) return -1;
        if (ca > cb) return 1;
        const ta = (a.title || '').toString().toLowerCase();
        const tb = (b.title || '').toString().toLowerCase();
        if (ta < tb) return -1;
        if (ta > tb) return 1;
        return 0;
    });

    // Determine if an item is completed
    const isCompleted = (item) => {
        if (!item || !item.id) return false;
        const id = (item.id || '').toString().toLowerCase();
        return completions.some(c => {
            const mid = (c.memberTrainingId || c.MemberTrainingId || c.MemberTrainingID || c.MemberTrainingId)?.toString?.().toLowerCase();
            return mid === id;
        });
    };

    const filtered = sortedItems
        .filter(i => selectedCategory === 'All' ? true : (i.category || 'Unspecified') === selectedCategory)
        .filter(i => {
            if (completionFilter === 'All') return true;
            const completed = isCompleted(i);
            if (completionFilter === 'To do') return !completed;
            if (completionFilter === 'Completed') return completed;
            return true;
        });

    const stripHtml = (html = '') => html.replace(/<[^>]+>/g, '');
    const snippet = (html = '', len = 150) => {
        const t = stripHtml(html || '').trim();
        if (t.length <= len) return t;
        return t.slice(0, len).trimEnd() + '...';
    };

    const deriveFileId = (item) => {
        // If contentFileName is like "{fileId}.mp4" or "{fileId}.pdf" return without extension
        //if (item?.contentFileName) return item.contentFileName.replace(/\.[^/.]+$/, '');
        // fallback: use id
        return item?.id || '';
    };

    const openVideo = (item) => {
        const fileId = deriveFileId(item);
        setVideoTrainingId(item.id);
        setVideoFileId(fileId);
        setVideoTitle(item.title || 'Video');
        setShowVideoModal(true);
    };

    const openHtml = (item) => {
        setHtmlTitle(item.title || 'Content');
        setHtmlContent(item.contentHtml || '<div>No content</div>');
        setShowHtmlModal(true);
    };

    const handleMarkCompleteClick = (item) => {
        setCompleteTarget(item);
        setShowCompleteModal(true);
    };

    const submitCompletion = async () => {
        if (!completeTarget) return;
        setSubmittingCompletion(true);
        try {
            const body = { MemberTrainingId: completeTarget.id };
            const res = await fetch(authConfig.api.urls.getMemberTrainingCompletionForMember, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // refresh completions
            const r2 = await fetch(authConfig.api.urls.getMemberTrainingCompletionsForMember, { credentials: 'include' });
            if (r2.ok) {
                const data = await r2.json();
                setCompletions(Array.isArray(data) ? data : []);
            }
            setShowCompleteModal(false);
            setCompleteTarget(null);
        } catch (err) {
            console.error('Failed to submit completion', err);
            alert('Failed to submit completion');
        } finally {
            setSubmittingCompletion(false);
        }
    };

    if (loading) return (
        <Container>
            <div className="text-center py-5">
                <Spinner animation="border" />
            </div>
        </Container>
    );

    if (error) return (
        <Container>
            <Alert variant="danger">{error}</Alert>
        </Container>
    );

    return (
        <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col><h5>Member Training</h5></Col>
                <Col className="text-end">
                    {/* Stack on xs, row on sm+; prevent wrapping on sm+ */}
                    <div className="d-flex flex-column flex-sm-row flex-sm-nowrap justify-content-end align-items-center gap-2">
                    <ButtonGroup aria-label="categories">
                        {categories.map(c => (
                            <Button key={c} variant={c === selectedCategory ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setSelectedCategory(c)}>{c}</Button>
                        ))}
                    </ButtonGroup>

                    <ButtonGroup aria-label="completion-filter">
                        {['All', 'To do', 'Completed'].map(f => (
                            <Button key={f} variant={f === completionFilter ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setCompletionFilter(f)}>{f}</Button>
                        ))}
                    </ButtonGroup>
                    </div>
                </Col>
            </Row>

            {filtered.length === 0 && (
                <Row>
                    <Col><Alert variant="info">No training items available for the selected category.</Alert></Col>
                </Row>
            )}

            <Row xs={1} sm={1} md={2} lg={3} xxl={3} className="g-3">
                {filtered.map(item => {
                    const type = (item.type || '').toString().toLowerCase();
                    const completed = isCompleted(item);
                    return (
                        <Col key={item.id}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex gap-2">
                                            <Badge bg="secondary">{item.category || 'Unspecified'}</Badge>
                                            <Badge bg="secondary">{item.type || ''}</Badge>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            
                                            {completed ? (
                                                <Badge bg="success">Completed</Badge>
                                            ) : (
                                                <Badge bg="warning">To do</Badge>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-2 w-100">
                                        <Card.Title className="mb-1 w-100">{item.title}</Card.Title>
                                    </div>

                                    <Card.Text className="flex-grow-1">{snippet(item.description || item.contentHtml || '')}</Card.Text>

                                    <div className="mt-3 d-flex gap-2 align-items-center">
                                        {type === 'video' && (
                                            <Button variant="outline-primary" onClick={() => openVideo(item)}>Play</Button>
                                        )}
                                        {type === 'pdf' && item.contentFileName && (
                                            <SecureFileLink
                                                url={`${authConfig.api.urls.getMemberTrainingPdfForMember}/${encodeURIComponent(item.id)}.pdf`}
                                                filename={item.contentFileName}
                                                type="button"
                                                variant="outline-primary"
                                                allowdownload={false }
                                            >
                                                Open PDF
                                            </SecureFileLink>
                                        )}
                                        {type === 'html' && (
                                            <Button variant="outline-primary" onClick={() => openHtml(item)} disabled={!item.contentHtml}>View</Button>
                                        )}

                                        <div className="ms-auto">
                                            
                                        </div>
                                        {!completed && (
                                            <Button size="sm" variant="outline-success" onClick={() => handleMarkCompleteClick(item)}>Mark complete</Button>
                                        )}
                                        
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            <Modal show={showVideoModal} onHide={() => setShowVideoModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{videoTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {videoTrainingId && videoFileId ? (
                        <VideoPlayer trainingId={videoTrainingId} fileId={videoFileId} isHls={true} />
                    ) : (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    )}
                </Modal.Body>
            </Modal>

            <Modal show={showHtmlModal} onHide={() => setShowHtmlModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{htmlTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                </Modal.Body>
            </Modal>

            <Modal show={showCompleteModal} onHide={() => { setShowCompleteModal(false); setCompleteTarget(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm completion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Please confirm that you have completed the training "{completeTarget?.title}"?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowCompleteModal(false); setCompleteTarget(null); }} disabled={submittingCompletion}>Cancel</Button>
                    <Button variant="primary" onClick={submitCompletion} disabled={submittingCompletion}>{submittingCompletion ? 'Submitting...' : 'Yes, I have completed this'}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default MemberTrainingList;
